// client/src/services/deploymentManager.ts
// Manage canary deployments and automatic rollbacks

import { versionManager } from './versionManagement';
import { healthMonitor } from './healthMonitoring';
import { analyticsCollector } from './analyticsCollector';

export interface CanaryConfig {
  initialPercentage: number; // Start with 1% of users
  stages: number[]; // [1, 10, 50, 100]
  monitoringDurationMs: number; // How long to monitor each stage
  errorRateThreshold: number; // Rollback if error rate > this
  criticalChunkThreshold: number; // Rollback if > N critical chunks
}

const DEFAULT_CANARY_CONFIG: CanaryConfig = {
  initialPercentage: 1,
  stages: [1, 10, 50, 100],
  monitoringDurationMs: 5 * 60 * 1000, // 5 minutes per stage
  errorRateThreshold: 0.05, // 5% error rate
  criticalChunkThreshold: 2,
};

class DeploymentManager {
  private canaryConfig: CanaryConfig;
  private currentStageIndex = 0;
  private stageStartTime = 0;
  private isMonitoring = false;
  private initialized = false;

  constructor(config: Partial<CanaryConfig> = {}) {
    this.canaryConfig = { ...DEFAULT_CANARY_CONFIG, ...config };
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;  }

  async startCanaryDeployment(version: string): Promise<boolean> {    this.isMonitoring = true;
    this.currentStageIndex = 0;

    for (let i = 0; i < this.canaryConfig.stages.length; i++) {
      this.currentStageIndex = i;
      const percentage = this.canaryConfig.stages[i];      // Record canary deployment
      versionManager.recordCanaryDeployment(version, percentage);
      analyticsCollector.trackDeploymentEvent(version, 'canary_stage_start', {
        stage: i + 1,
        percentage,
      });

      // Monitor this stage
      const isHealthy = await this.monitorStage(version);

      if (!isHealthy) {
        console.error(`[DeploymentManager] Canary stage ${i + 1} failed health check`);
        analyticsCollector.trackDeploymentEvent(version, 'canary_stage_failed', {
          stage: i + 1,
          percentage,
        });

        // Trigger rollback
        await this.rollback(version, `Canary stage ${i + 1} failed`);
        return false;
      }      analyticsCollector.trackDeploymentEvent(version, 'canary_stage_passed', {
        stage: i + 1,
        percentage,
      });
    }    this.isMonitoring = false;
    return true;
  }

  private async monitorStage(version: string): Promise<boolean> {
    this.stageStartTime = Date.now();
    const startHealth = healthMonitor.getAppHealth();

    // Wait for monitoring duration
    await new Promise(resolve => setTimeout(resolve, this.canaryConfig.monitoringDurationMs));

    const endHealth = healthMonitor.getAppHealth();

    // Check health metrics
    const errorRate = endHealth.errorRate;
    const criticalChunks = endHealth.chunkCritical;    const isHealthy =
      errorRate <= this.canaryConfig.errorRateThreshold &&
      criticalChunks <= this.canaryConfig.criticalChunkThreshold;

    return isHealthy;
  }

  private async rollback(version: string, reason: string): Promise<void> {
    console.error(`[DeploymentManager] Rolling back from ${version}: ${reason}`);

    versionManager.recordDeploymentMetrics(version, {
      version,
      errorRate: healthMonitor.getAppHealth().errorRate,
      affectedUsers: 0, // Would come from backend
      criticalChunks: healthMonitor.getAppHealth().chunkCritical,
      shouldRollback: true,
      rollbackReason: reason,
    });

    analyticsCollector.trackDeploymentEvent(version, 'rollback', {
      reason,
    });
  }

  isCanaryDeploymentInProgress(): boolean {
    return this.isMonitoring;
  }

  getCurrentCanaryPercentage(): number {
    if (!this.isMonitoring) return 0;
    return this.canaryConfig.stages[this.currentStageIndex] || 0;
  }
}

export const deploymentManager = new DeploymentManager();

